import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getHistoryLabPanel } from "../../../../common/network/Api";
import PageLoader from "../../../../common/ui/PageLoader";
import OrderDetailsView from "../orders/OrderDetailsView";
import HistoryNotReadyState from "./HistoryNotReadyState";
import useHistoryScope from "./useHistoryScope";
import { invalidateHistoryList } from "./useHistoryList";

/**
 * `…/assessments/lab/:orderId` — one lab order's detail.
 *
 * Fetches the one panel by id (`GET /history/labpanels/{id}`, items included)
 * rather than reading a row handed down from the table, so pasting an order
 * URL into a fresh tab works — the old Orders screen resolved only against
 * its already-loaded list.
 *
 * The way back to the tab is the breadcrumb's, derived from the URL
 * (`useBackNavigation`), so this route supplies no back handler.
 */
const LabOrderDetailRoute = () => {
  const { orderId } = useParams();
  const { kind, basePath } = useHistoryScope();
  const location = useLocation();
  const navigate = useNavigate();

  const [order, setOrder] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(
    async (silent = false) => {
      if (!orderId) {
        setLoading(false);
        setNotFound(true);
        return;
      }
      try {
        if (!silent) setLoading(true);
        const panel = await getHistoryLabPanel(orderId);
        // The row id is the customer-panel `id`, never `userLabPanelID`.
        const found = panel && String(panel.id) === String(orderId);
        setOrder(found ? panel : null);
        setNotFound(!found);
      } catch (error) {
        console.error("Error loading lab order:", error);
        setNotFound(true);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [orderId]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Every action here can move the order's status: the Assessments list's
  // cached rows are wrong from now on, so the way back must refetch them.
  const refreshAfterChange = () => {
    invalidateHistoryList("labpanels");
    load(true);
  };

  if (loading) return <PageLoader />;

  if (notFound || !order) {
    return (
      <HistoryNotReadyState
        title="Order not found"
        text="This lab order is no longer available. Go back to Assessments to see the current list."
      />
    );
  }

  return (
    <div style={styles.root}>
      <OrderDetailsView
        order={order}
        // Only the cross-client page needs the badge; the workspace shells
        // already name the subject in their top bar.
        showClientBadge={kind === "company"}
        // The prescription this order was ordered from, in the same History
        // scope; `location.search` keeps the workspace's `?id=` client.
        prescriptionLink={
          order.customerPrescriptionID
            ? `${basePath}/prescription/${order.customerPrescriptionID}${location.search}`
            : undefined
        }
        // Approving changes the order's status, so re-read it in place.
        onApproved={refreshAfterChange}
        onRefetch={refreshAfterChange}
        // Answering the questions saves the prescription, which re-creates
        // the panel under a new id: swap the URL to it (no history entry,
        // the old id is gone) and `load` re-runs on the new param.
        onPanelReplaced={(newId) => {
          invalidateHistoryList("labpanels");
          navigate(`${basePath}/assessments/lab/${newId}${location.search}`, {
            replace: true,
          });
        }}
      />
    </div>
  );
};

export default LabOrderDetailRoute;

const styles = {
  root: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
    boxSizing: "border-box" as "border-box",
  },
};
